import { Close } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import { enUS, pl } from 'date-fns/locale';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** The three states the Notatka column shows. */
export const NOTE_STATUSES = ['SENT', 'DRAFT', 'NONE'] as const;

export type NoteStatus = (typeof NOTE_STATUSES)[number];

export interface VisitFilters {
  userName?: string;
  profileName?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  /** Zamknięto od — filters when the visit was closed, not when it was booked. */
  closedFrom?: Date | null;
  /** Zamknięto do. */
  closedTo?: Date | null;
  noteStatuses?: NoteStatus[];
}

interface VisitHistoryFiltersProps {
  onApplyFilters: (filters: VisitFilters) => void;
  onRemoveFilter: (filterKey: keyof VisitFilters) => void;
  initialFilters?: VisitFilters;
  isOpen: boolean;
}

export default function VisitHistoryFilters({
  onApplyFilters,
  onRemoveFilter,
  initialFilters,
  isOpen,
}: VisitHistoryFiltersProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith('pl') ? pl : enUS;
  const [userName, setUserName] = useState(initialFilters?.userName || '');
  const [profileName, setProfileName] = useState(
    initialFilters?.profileName || '',
  );
  const startDate = initialFilters?.startDate ?? null;
  const endDate = initialFilters?.endDate ?? null;
  const closedFrom = initialFilters?.closedFrom ?? null;
  const closedTo = initialFilters?.closedTo ?? null;

  const noteStatuses = initialFilters?.noteStatuses ?? [];

  const userNameTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const profileNameTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const applyFilters = (overrides: Partial<VisitFilters> = {}) => {
    onApplyFilters({
      userName: userName.trim() || undefined,
      profileName: profileName.trim() || undefined,
      startDate,
      endDate,
      closedFrom,
      closedTo,
      noteStatuses: noteStatuses.length ? noteStatuses : undefined,
      ...overrides,
    });
  };

  const latestApplyFiltersRef = useRef(applyFilters);
  latestApplyFiltersRef.current = applyFilters;

  useEffect(
    () => () => {
      if (userNameTimeoutRef.current) clearTimeout(userNameTimeoutRef.current);
      if (profileNameTimeoutRef.current) clearTimeout(profileNameTimeoutRef.current);
    },
    [],
  );

  const handleUserNameFilter = (value: string) => {
    setUserName(value);

    if (userNameTimeoutRef.current) {
      clearTimeout(userNameTimeoutRef.current);
    }

    userNameTimeoutRef.current = setTimeout(() => {
      latestApplyFiltersRef.current({ userName: value.trim() || undefined });
    }, 350);
  };

  const handleProfileNameFilter = (value: string) => {
    setProfileName(value);

    if (profileNameTimeoutRef.current) {
      clearTimeout(profileNameTimeoutRef.current);
    }

    profileNameTimeoutRef.current = setTimeout(() => {
      latestApplyFiltersRef.current({ profileName: value.trim() || undefined });
    }, 350);
  };

  const handleStartDateFilter = (newValue: Date | null) => {
    applyFilters({ startDate: newValue });
  };

  const handleEndDateFilter = (newValue: Date | null) => {
    applyFilters({ endDate: newValue });
  };

  const handleClosedFromFilter = (newValue: Date | null) => {
    applyFilters({ closedFrom: newValue });
  };

  const handleClosedToFilter = (newValue: Date | null) => {
    applyFilters({ closedTo: newValue });
  };

  const handleNoteStatusFilter = (selected: NoteStatus[]) => {
    applyFilters({ noteStatuses: selected.length ? selected : undefined });
  };

  const noteStatusLabel = (status: NoteStatus) =>
    String(
      t(
        {
          SENT: 'visit.notesStatusSent',
          DRAFT: 'visit.notesStatusDraft',
          NONE: 'visit.notesStatusNone',
        }[status],
      ),
    );

  const handleClear = () => {
    setUserName('');
    setProfileName('');

    if (userNameTimeoutRef.current) {
      clearTimeout(userNameTimeoutRef.current);
    }
    if (profileNameTimeoutRef.current) {
      clearTimeout(profileNameTimeoutRef.current);
    }

    onApplyFilters({});
  };

  const getFilterLabel = (key: keyof VisitFilters, value: any): string => {
    switch (key) {
      case 'userName':
        return String(t('visit.historyFilterOwnerChip', { name: value }));
      case 'profileName':
        return String(t('visit.historyFilterPetChip', { name: value }));
      case 'startDate':
        return String(
          t('visit.historyDateFromChip', {
            date: format(new Date(value), 'dd.MM.yyyy', { locale: dateLocale }),
          }),
        );
      case 'endDate':
        return String(
          t('visit.historyDateToChip', {
            date: format(new Date(value), 'dd.MM.yyyy', { locale: dateLocale }),
          }),
        );
      case 'closedFrom':
        return String(
          t('visit.historyClosedFromChip', {
            date: format(new Date(value), 'dd.MM.yyyy', { locale: dateLocale }),
          }),
        );
      case 'closedTo':
        return String(
          t('visit.historyClosedToChip', {
            date: format(new Date(value), 'dd.MM.yyyy', { locale: dateLocale }),
          }),
        );
      case 'noteStatuses':
        return String(
          t('visit.noteStatusChip', {
            statuses: (value as NoteStatus[]).map(noteStatusLabel).join(', '),
          }),
        );
      default:
        return '';
    }
  };

  const renderActiveFilters = () => {
    const filters = [
      initialFilters?.userName && {
        key: 'userName' as const,
        label: getFilterLabel('userName', initialFilters.userName),
      },
      initialFilters?.profileName && {
        key: 'profileName' as const,
        label: getFilterLabel('profileName', initialFilters.profileName),
      },
      initialFilters?.startDate && {
        key: 'startDate' as const,
        label: getFilterLabel('startDate', initialFilters.startDate),
      },
      initialFilters?.endDate && {
        key: 'endDate' as const,
        label: getFilterLabel('endDate', initialFilters.endDate),
      },
      initialFilters?.closedFrom && {
        key: 'closedFrom' as const,
        label: getFilterLabel('closedFrom', initialFilters.closedFrom),
      },
      initialFilters?.closedTo && {
        key: 'closedTo' as const,
        label: getFilterLabel('closedTo', initialFilters.closedTo),
      },
      initialFilters?.noteStatuses?.length && {
        key: 'noteStatuses' as const,
        label: getFilterLabel('noteStatuses', initialFilters.noteStatuses),
      },
    ].filter(Boolean) as Array<{ key: keyof VisitFilters; label: string }>;

    if (filters.length === 0) return null;

    return (
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        gap={1}
        sx={{ px: 3, pb: 2 }}
      >
        {filters.map((filter) => (
          <Chip
            key={filter.key}
            label={filter.label}
            onDelete={() => onRemoveFilter(filter.key)}
            size="small"
            color="primary"
            variant="outlined"
          />
        ))}
        <Chip
          label={t('visit.filtersClearAll')}
          onClick={handleClear}
          size="small"
          color="default"
          variant="outlined"
          deleteIcon={<Close />}
          onDelete={handleClear}
        />
      </Stack>
    );
  };

  return (
    <Box>
      <Stack sx={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
        {renderActiveFilters()}
      </Stack>
      <Collapse in={isOpen}>
        <Box
          sx={{
            px: 3,
            pt: 1,
            pb: 3,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(10, 1fr)',
            },
            gap: 2,
          }}
        >
          <Box
            sx={{
              gridColumn: { xs: '1', sm: '1', md: 'span 1', lg: 'span 3' },
            }}
          >
            <TextField
              // Both consumers of this panel are staff-only, and it sits beside
              // a column that reads Pacjent — the two must not disagree.
              label={t('visit.tablePatient')}
              value={profileName}
              onChange={(e) => handleProfileNameFilter(e.target.value)}
              size="small"
              fullWidth
            />
          </Box>

          <Box
            sx={{
              gridColumn: { xs: '1', sm: '1', md: 'span 1', lg: 'span 3' },
            }}
          >
            <TextField
              label={t('visit.historyOwnerField')}
              value={userName}
              onChange={(e) => handleUserNameFilter(e.target.value)}
              size="small"
              fullWidth
            />
          </Box>

          <Box
            sx={{
              gridColumn: { xs: '1', sm: '1', md: 'span 1', lg: 'span 2' },
            }}
          >
            <DatePicker
              label={t('visit.historyDateFromLabel')}
              value={startDate}
              onChange={(newValue) =>
                handleStartDateFilter(newValue as Date | null)
              }
              renderInput={(params) => (
                <TextField {...params} size="small" fullWidth />
              )}
            />
          </Box>

          <Box
            sx={{
              gridColumn: { xs: '1', sm: '1', md: 'span 1', lg: 'span 2' },
            }}
          >
            <DatePicker
              label={t('visit.historyDateToLabel')}
              value={endDate}
              onChange={(newValue) =>
                handleEndDateFilter(newValue as Date | null)
              }
              minDate={startDate || undefined}
              renderInput={(params) => (
                <TextField {...params} size="small" fullWidth />
              )}
            />
          </Box>

          <Box
            sx={{
              gridColumn: { xs: '1', sm: '1', md: 'span 1', lg: 'span 2' },
            }}
          >
            <DatePicker
              label={t('visit.historyClosedFromLabel')}
              value={closedFrom}
              onChange={(newValue) =>
                handleClosedFromFilter(newValue as Date | null)
              }
              renderInput={(params) => (
                <TextField {...params} size="small" fullWidth />
              )}
            />
          </Box>

          <Box
            sx={{
              gridColumn: { xs: '1', sm: '1', md: 'span 1', lg: 'span 2' },
            }}
          >
            <DatePicker
              label={t('visit.historyClosedToLabel')}
              value={closedTo}
              onChange={(newValue) =>
                handleClosedToFilter(newValue as Date | null)
              }
              minDate={closedFrom || undefined}
              renderInput={(params) => (
                <TextField {...params} size="small" fullWidth />
              )}
            />
          </Box>

          <Box
            sx={{
              gridColumn: { xs: '1', sm: '1', md: 'span 2', lg: 'span 10' },
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="visit-history-note-status-label">
                {t('visit.noteStatusFilterLabel')}
              </InputLabel>
              <Select
                multiple
                labelId="visit-history-note-status-label"
                label={t('visit.noteStatusFilterLabel')}
                value={noteStatuses}
                data-testid="visit-history-note-status-filter"
                onChange={(event) =>
                  handleNoteStatusFilter(event.target.value as NoteStatus[])
                }
                renderValue={(selected) =>
                  selected.map(noteStatusLabel).join(', ')
                }
              >
                {NOTE_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox
                      size="small"
                      checked={noteStatuses.includes(status)}
                    />
                    <ListItemText primary={noteStatusLabel(status)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
